import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { OptionConnectCard } from "./OptionConnectCard";   // OPT-B2b
import { SeatDetailModal } from "./SeatDetailModal";   // CHANAV-3
import { CtripListingDrawer } from "./CtripListingDrawer";   // CTRIP-P2
import { WanleBindingTable } from "./WanleBindingTable";   // WANLE-3
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight } from "lucide-react";
import { ELIGIBLE_PRODUCTS_QUERY_KEY, listMappings, MAPPINGS_QUERY_KEY, patchMapping, setExtraVehicle, toggleProductMapping } from "@/services/channelCenter";
import { AlertTriangle } from "lucide-react";
import { useCan } from "@/auth/permissions";
import { CTRIP_LOOKUPS_QUERY_KEY, ctripLookupsApi } from "@/services/ctripLookups";   // CTRIP-P1
import { FixEnglishLink, isMissingEnglish } from "@/components/MissingEnglish";
import type { CtripListingOptionSummary, CtripListingSummary, ListingPlatform, MappingProductRow, MappingTierRow } from "@/services/types/channelCenter";

function Pill({ tone, children }: { tone: "green" | "amber" | "gray" | "red"; children: React.ReactNode }) {
  const cls = {
    green: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    gray: "bg-slate-100 text-slate-600",
    red: "bg-rose-50 text-rose-600",
  }[tone];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[12px] font-medium ${cls}`}>
      <span className="h-[7px] w-[7px] rounded-full bg-current" />
      {children}
    </span>
  );
}

/** GYGPAUSE-1 · 下次试探时间：本地时间「10-21 07:00」 */
function fmtProbe(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function fmtUsd(cents: number | null, decimals = true): string {
  if (cents == null) return "—";
  return `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: decimals ? 2 : 0, maximumFractionDigits: decimals ? 2 : 0 })}`;
}

// LISTCTR-1 · 票种（样稿：上架中心-样稿.html 右侧抽屉）。所有票种同价（价格按售价管理中心 api 渠道价）。
const TICKET_TYPES: { code: string; cn: string; hint: string }[] = [
  { code: "ADULT", cn: "成人", hint: "必选" },
  { code: "CHILD", cn: "儿童", hint: "同成人价" },
  { code: "YOUTH", cn: "青少年", hint: "同成人价" },
  { code: "INFANT", cn: "婴儿", hint: "同成人价（占座）" },
  { code: "SENIOR", cn: "长者", hint: "同成人价" },
];
const TICKET_CN: Record<string, string> = Object.fromEntries(TICKET_TYPES.map((t) => [t.code, t.cn]));

function Checkbox({ on, onClick, readOnly }: { on: boolean; onClick: () => void; readOnly?: boolean }) {
  if (readOnly) {
    // 没有「上架中心 · 管理」权限：只显示勾选状态，不能点
    return (
      <span
        aria-label={on ? "已勾选推送" : "未勾选"}
        className={`inline-block h-[17px] w-[17px] shrink-0 rounded-[4px] border-[1.5px] align-middle ${on ? "border-blue-300 bg-blue-300 text-white" : "border-slate-300 bg-white"}`}
      >
        {on && (
          <svg viewBox="0 0 12 12" className="h-full w-full p-0.5"><path d="M2 6l2.5 2.5L10 3" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
        )}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-[17px] w-[17px] shrink-0 rounded-[4px] border-[1.5px] transition ${
        on ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300 bg-white"
      }`}
    >
      {on && (
        <svg viewBox="0 0 12 12" className="h-full w-full p-0.5"><path d="M2 6l2.5 2.5L10 3" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
      )}
    </button>
  );
}

/**
 * CHANAV-2 E · 展开产品后的「满车加售（每单至少 4 人）」一行（契约：样稿 满车加售-样稿.html + API_MAP-满车加售.md）。
 * 开关 = 按每个启用中的常规档生成 / 停用 -X 加售编码；关闭走页内确认（不用浏览器弹窗）。这里不加车——
 * 收到加售单后由 OP 在调度中心「加车」。
 */
function ExtraVehicleRow({
  row, platformName, busy, canManage, onToggle, onMarkBound,
}: {
  row: MappingProductRow; platformName: string; busy: boolean; canManage: boolean;
  onToggle: (enabled: boolean) => void; onMarkBound: (mappingId: string) => void;
}) {
  const ev = row.extra_vehicle!;
  const [confirmClose, setConfirmClose] = useState(false);
  const status = !ev.enabled
    ? <Pill tone="gray">未开</Pill>
    : ev.seats_now == null
      ? <Pill tone="green">已开 · 暂无可售团期</Pill>
      : ev.seats_now > 0
        ? <Pill tone="amber">已开 · 现在余位 {ev.seats_now}（常规车已满）</Pill>
        : <Pill tone="green">已开 · 现在余位 0（常规车还有位）</Pill>;
  return (
    <div className="relative flex flex-col gap-2.5 rounded-[10px] bg-[#FAFCFF] px-3.5 py-3" data-testid="extra-vehicle-row">
      <div className="flex flex-wrap items-center gap-2.5">
        {canManage && (
          <button
            type="button" role="switch" aria-checked={ev.enabled} aria-label="满车加售" disabled={busy}
            onClick={() => (ev.enabled ? setConfirmClose(true) : onToggle(true))}
            className={`relative h-5 w-9 shrink-0 rounded-full transition ${ev.enabled ? "bg-blue-600" : "bg-slate-300"} ${busy ? "opacity-50" : ""}`}
          >
            <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${ev.enabled ? "left-[18px]" : "left-0.5"}`} />
          </button>
        )}
        <b className="text-[13.5px] text-slate-900">满车加售（每单至少 4 人）</b>
        {status}
      </div>
      <div className="max-w-[90ch] text-[12.5px] text-slate-500">
        常规车坐满后，平台上用这个选项继续接 <b className="text-slate-900">4 人及以上</b>的单（够加一台车的利润）。平时余位是 0，不影响常规选项。
        打开后会为上面每个选项生成一个加售编码。这里<b className="text-slate-900">不加车</b>，收到单后由 OP 在调度中心加车。
      </div>
      {ev.enabled && (
        <>
          <table className="w-full text-[12.5px]" data-testid="extra-vehicle-tiers">
            <tbody>
              {ev.tiers.map((t) => (
                <tr key={t.mapping_id} className="border-t border-slate-100">
                  <td className="py-2 pl-3 text-slate-600">└ {t.occupancy_label} · 满车加售</td>
                  <td><span className="rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[11.5px]">{t.external_product_id}</span></td>
                  <td className="tabular-nums">{fmtUsd(t.list_price_cents)}</td>
                  <td>
                    {t.platform_bound ? (
                      <span className="text-emerald-600">{platformName} option 已挂 ✓</span>
                    ) : canManage ? (
                      <button type="button" onClick={() => onMarkBound(t.mapping_id)} className="text-amber-600 hover:underline">
                        未挂 · 标记为已在 {platformName} 后台建 option
                      </button>
                    ) : (
                      <span className="text-amber-600">未挂</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-700">
            还要做：在 {platformName} 后台为上面每个加售编码各建一个选项，<b>每单最少人数设成 4</b>，建好后在这里点「标记为已在 {platformName} 后台建 option」。
          </div>
          {confirmClose && (
            <div role="alert" className="flex flex-wrap items-center gap-2 rounded-[10px] border border-rose-200 bg-rose-50 px-3 py-2.5 text-[13px] text-rose-800">
              <span>关闭后这 {ev.tiers.length} 个加售选项停止推送余位，平台上的选项不会被删除；已收的加售单不受影响。确认关闭？</span>
              <span className="flex-1" />
              <button type="button" onClick={() => setConfirmClose(false)} className="rounded-md border border-slate-200 bg-white px-3 py-1 text-[12.5px] text-slate-700">取消</button>
              <button
                type="button" disabled={busy}
                onClick={() => { setConfirmClose(false); onToggle(false); }}
                className="rounded-md bg-rose-600 px-3 py-1 text-[12.5px] font-semibold text-white"
              >关闭满车加售</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** 票种抽屉（右侧）：勾平台上卖哪些票种；成人必选不可取消；对该产品所有常规选项一起改，满车加售跟随常规兄弟（后端同步）。 */
function TicketDrawer({
  row, platformCode, onClose, onSaved,
}: { row: MappingProductRow; platformCode: string; onClose: () => void; onSaved: () => void }) {
  const initial = new Set(row.categories && row.categories.length ? row.categories : ["ADULT"]);
  const [picked, setPicked] = useState<Set<string>>(initial);
  const save = useMutation({
    mutationFn: async () => {
      const category_map = Object.fromEntries(TICKET_TYPES.filter((t) => picked.has(t.code)).map((t) => [t.code, {}])) as Record<string, Record<string, never>>;
      for (const tier of row.tiers as MappingTierRow[]) {
        await patchMapping(platformCode, tier.mapping_id, { category_map });
      }
    },
    onSuccess: onSaved,
  });
  const toggle = (code: string) => {
    if (code === "ADULT") return;
    setPicked((prev) => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });
  };
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/35" onClick={onClose} data-testid="ticket-drawer-bg">
      <div className="flex h-full w-full max-w-[420px] flex-col gap-3.5 overflow-y-auto bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="ticket-drawer">
        <div className="flex items-center text-[16px] font-semibold text-slate-900">
          票种 · <span className="ml-1.5 font-medium text-slate-600">{row.product_name} · {row.product_code}</span>
          <button type="button" aria-label="关闭" onClick={onClose} className="ml-auto text-[18px] text-slate-400 hover:text-slate-600">✕</button>
        </div>
        <div className="text-[13px] text-slate-500">
          平台上卖哪些票种，这里就勾哪些。没勾的票种，平台来订会被拒。价格都按售价管理中心的渠道价，所有票种同价。
        </div>
        {TICKET_TYPES.map((t) => (
          <label key={t.code} className={`flex items-center gap-2.5 rounded-[10px] border border-slate-200 px-3 py-2.5 ${t.code === "ADULT" ? "" : "cursor-pointer"}`}>
            <input
              type="checkbox" checked={picked.has(t.code)} disabled={t.code === "ADULT"}
              onChange={() => toggle(t.code)} aria-label={`${t.cn} ${t.code}`}
            />
            <div>
              <div className="text-[13.5px] font-semibold text-slate-900">{t.cn} {t.code}</div>
              <div className="text-[12px] text-slate-400">{t.hint}</div>
            </div>
          </label>
        ))}
        {save.isError && (
          <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700">
            保存失败：{(save.error as Error).message}
          </div>
        )}
        <div className="mt-auto flex gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 bg-white px-4 py-1.5 text-[13px] text-slate-600">取消</button>
          <button type="button" disabled={save.isPending} onClick={() => save.mutate()} className="rounded-lg bg-blue-600 px-4 py-1.5 text-[13px] font-semibold text-white disabled:opacity-50">
            {save.isPending ? "保存中…" : "保存"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** CTRIP-P2 · 「携程建档」列（样稿 ①）：状态 pill + 小字 + 「建档详情 / 去建档」；一日游行显示不接入原因。 */
const CTRIP_STATUS: Record<string, { tone: "green" | "amber" | "gray" | "red"; text: string }> = {
  missing: { tone: "red", text: "未建档" }, draft: { tone: "red", text: "未建档" }, auditing: { tone: "amber", text: "携程审核中" }, rejected: { tone: "red", text: "携程驳回" },
  on_sale: { tone: "green", text: "在售" }, content_stale: { tone: "amber", text: "在售 · 内容改过" }, offline: { tone: "gray", text: "已下线" },
};
function ctripOption(row: MappingProductRow): (CtripListingOptionSummary) | null {
  const list = row.ctrip_listings ?? [];
  return list.find((o) => o.is_default) ?? list[0] ?? null;
}
function CtripListingCell({ s, onOpen }: { s: CtripListingSummary | null | undefined; onOpen: () => void }) {
  if (!s) return <span className="text-slate-400">—</span>;
  if (s.status === "blocked") return <span className="text-[12px] text-slate-400" data-testid="ctrip-blocked">{s.blocked_reason ?? "未接入"}</span>;
  const c = CTRIP_STATUS[s.status] ?? CTRIP_STATUS.missing;
  const stamp = (iso: string | null) => (iso ? new Date(iso) : null);
  const sub = s.status === "rejected" ? s.reject_reason : s.status === "auditing" ? (stamp(s.submitted_at) ? `${fmtProbe(s.submitted_at as string)} 提交` : null)
    : s.status === "on_sale" ? (s.last_price_push_at || s.last_inventory_push_at ? `价 ${s.last_price_push_at ? fmtProbe(s.last_price_push_at) : "—"} · 余位 ${s.last_inventory_push_at ? fmtProbe(s.last_inventory_push_at) : "—"}` : null)
    : s.status === "missing" || s.status === "draft" ? "勾选会打开建档，补齐才能推" : null;
  return (
    <div data-testid="ctrip-listing-cell">
      <Pill tone={c.tone}>{c.text}</Pill>
      {sub && <div className="mt-0.5 text-[11.5px] text-slate-400">{sub}</div>}
      <button type="button" onClick={onOpen} className="mt-0.5 text-[12px] font-medium text-blue-600 hover:underline">{s.status === "missing" || s.status === "draft" ? "去建档" : "建档详情"}</button>
    </div>
  );
}

export function ListingCenter({ platforms }: { platforms: ListingPlatform[] }) {
  const canManage = useCan("listing.mappings.manage");   // 勾选 / 票种 / 标为已挂 / 满车加售开关
  const [ticketRow, setTicketRow] = useState<MappingProductRow | null>(null);
  const [seatRow, setSeatRow] = useState<MappingProductRow | null>(null);   // CHANAV-3 · 余位明细弹窗
  const [ctripRow, setCtripRow] = useState<MappingProductRow | null>(null);   // CTRIP-P2 · 携程建档抽屉
  // OPT-B2b · 按选项连接：?product_id=&option_id= 进来直接定位（产品选项页「去上架中心连接」）；产品行的「按选项连接」只带 product_id
  const [sp, setSp] = useSearchParams();
  const focusProduct = sp.get("product_id");
  const focusOption = sp.get("option_id");
  const closeFocus = () => setSp((prev) => { const n = new URLSearchParams(prev); n.delete("product_id"); n.delete("option_id"); return n; }, { replace: true });
  const live = platforms.filter((p) => p.status === "live");
  // LISTSW-1：默认选中第一个已开闸平台；一个都没开闸就选第一个平台（未开闸的也能先勾选产品）
  const [picked, setActiveCode] = useState<string | undefined>(undefined);
  // 默认选中：第一个已开闸的；都没开闸就选「有映射行的第一个平台」（表格里才有东西可看）；再没有就第一个
  const activeCode = picked && platforms.some((p) => p.code === picked) ? picked : (live[0] ?? platforms.find((p) => (p.mapped_product_count ?? 0) > 0) ?? platforms[0])?.code;
  const activePlatform = platforms.find((p) => p.code === activeCode);
  const pname = activePlatform?.display_name ?? "";
  const ordersOnly = !!activePlatform?.orders_only;   // 携程这类只接订单的平台：不说「推送中」，也不提 GYG option
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const isCtrip = activeCode === "ctrip";   // CTRIP-P1 · 携程页签：说明条 + 对照表 / 资料入口；一日游行置灰 · CTRIP-P2 · 建档列 + 抽屉
  const isWanle = activeCode === "ctrip_wanle";   // WANLE-3 · 携程玩乐页签：只收一日游 + 门票，每选项一行，填携程编号绑定
  const ctripSummaryQ = useQuery({ queryKey: [...CTRIP_LOOKUPS_QUERY_KEY, "summary"], queryFn: ctripLookupsApi.summary, enabled: isCtrip });
  const ctripPending = ctripSummaryQ.data?.pending_total;
  // CHANAV-1 · 打开映射时后端带回的只读提示（挂线路的产品未来 30 天没有车次…），显示在表格上方，不拦
  const [notice, setNotice] = useState<{ name: string; warnings: string[] } | null>(null);
  // ENNAME-3 · 打开推送时没有真英文名 → 409，表格上方红条 + 「去补」（跳产品详情）
  const [blocked, setBlocked] = useState<{ id: string; name: string; message: string } | null>(null);
  const onOpenError = (e: unknown, row: MappingProductRow) => {
    setNotice(null);
    const inner = ((e as { detail?: { detail?: { code?: string; message?: string } } })?.detail?.detail);
    if (inner?.code === "INSTANT_CONFIRM_REQUIRED") { setInstantBlocked({ id: row.product_id, name: row.product_name, message: inner.message ?? "" }); setBlocked(null); return; }   // CONFIRM-1
    setInstantBlocked(null);
    setBlocked(isMissingEnglish(e) ? { id: row.product_id, name: row.product_name, message: (e as Error).message } : null);
  };
  // CONFIRM-1 · 「需处理」筛选：已上架但产品没勾即时确认的（只标红，不自动下架）
  const [onlyAttention, setOnlyAttention] = useState(false);
  const [instantBlocked, setInstantBlocked] = useState<{ id: string; name: string; message: string } | null>(null);
  const qc = useQueryClient();

  const mappingsQ = useQuery({
    queryKey: [...MAPPINGS_QUERY_KEY, activeCode],
    queryFn: () => listMappings(activeCode!),
    enabled: !!activeCode,
  });

  // 勾选 / 取消 = 产品详情「上架到哪些平台」开关同一个 service（toggleProductMapping），改完两处的查询一起失效
  const refresh = () => {
    qc.invalidateQueries({ queryKey: MAPPINGS_QUERY_KEY });
    qc.invalidateQueries({ queryKey: ELIGIBLE_PRODUCTS_QUERY_KEY });
  };
  const toggleMut = useMutation({
    mutationFn: (vars: { row: MappingProductRow; on: boolean }) => toggleProductMapping(activeCode!, vars.row, vars.on),
    onSuccess: (warnings, vars) => {
      setBlocked(null);
      setNotice(warnings.length ? { name: vars.row.product_name, warnings } : null);
      refresh();
    },
    onError: (e, vars) => { onOpenError(e, vars.row); refresh(); },
  });
  const extraMut = useMutation({
    mutationFn: (vars: { row: MappingProductRow; enabled: boolean }) => setExtraVehicle(activeCode!, vars.row.product_id, vars.enabled),
    onSuccess: (res, vars) => {
      setBlocked(null);
      setNotice(res.warnings?.length ? { name: vars.row.product_name, warnings: res.warnings } : null);
      refresh();
    },
    onError: (e, vars) => onOpenError(e, vars.row),
  });
  const patchMut = useMutation({
    mutationFn: (vars: { mappingId: string; platform_bound?: boolean }) =>
      patchMapping(activeCode!, vars.mappingId, { platform_bound: vars.platform_bound }),
    onSuccess: refresh,
  });

  // CTRIP-P2 · 提交到携程成功 → 才建映射（已经是勾选态的不重复建）
  function onCtripSubmitted() {
    const row = ctripRow;
    if (row && row.overall_status !== "active" && activeCode) toggleMut.mutate({ row, on: true });
    else refresh();
  }

  function toggleExpand(productId: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  }

  function handleCheckToggle(row: MappingProductRow) {
    const willUncheck = row.overall_status === "active";
    // CTRIP-P2 · 携程多日游：勾「推送」= 打开建档抽屉，不直接建映射（提交成功时才建，见 onCtripSubmitted）
    if (isCtrip && !willUncheck && row.ctrip_listing && row.ctrip_listing.status !== "blocked") { setCtripRow(row); return; }
    if (willUncheck && !window.confirm("取消后该平台将查不到此产品，已产生订单不受影响。确认取消？")) return;
    toggleMut.mutate({ row, on: !willUncheck });
  }

  const allRows = mappingsQ.data ?? [];
  const attentionCount = allRows.filter((r) => r.needs_attention).length;
  const instantRequired = allRows.some((r) => r.instant_required);
  const rows = onlyAttention ? allRows.filter((r) => r.needs_attention) : allRows;

  return (
    <div className="mt-5 rounded-[14px] border border-slate-200 bg-white p-5">
      <h3 className="text-[15px] font-semibold text-slate-900">产品推送</h3>
      <div className="mb-3.5 mt-1 text-[12px] text-slate-400">
        选择平台后勾选允许该平台售卖的产品 · 价格为定价中心 api 渠道口径只读镜像（此处不设价）
      </div>

      <div className="mb-3.5 flex flex-wrap gap-2">
        {platforms.map((p) => (
          <button
            key={p.code}
            type="button"
            onClick={() => setActiveCode(p.code)}
            className={`rounded-md px-3 py-1.5 text-[12px] font-medium transition ${
              activeCode === p.code
                ? "bg-blue-600 text-white"
                : p.status === "live"
                  ? "border border-slate-200 text-slate-600 hover:bg-slate-50"
                  : "border border-slate-200 text-slate-400 hover:bg-slate-50"
            }`}
          >
            {p.display_name}{p.status === "live" ? "" : "（未开闸）"}
          </button>
        ))}
      </div>

      {activePlatform && activePlatform.status !== "live" && (
        <div role="status" className="mb-3.5 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-700">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>这个平台还没开闸：勾选只是准备上架，不会对外售卖。开闸在「渠道中心 → 平台详情」，由老板 / 技术管理员操作。</span>
        </div>
      )}

      {isCtrip && (
        <div role="note" data-testid="ctrip-note" className="mb-3.5 flex flex-wrap items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-[12.5px] text-sky-800">
          <span>携程这套接口只能上<b>多日游</b>。一日游、门票走携程「玩乐」页签（另一套接口），这里置灰。产品资料一律在我们系统改，推过去会覆盖携程后台的手工修改。</span>
          <span className="ml-auto flex gap-1.5">
            <Link to="/admin/listing/ctrip-lookups" data-testid="ctrip-lookups-link" className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50">携程对照表{ctripPending == null ? "" : ` · 待对 ${ctripPending}`}</Link>
            <Link to="/admin/api-integration/ctrip?tab=profile" data-testid="ctrip-profile-link" className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50">携程资料</Link>
          </span>
        </div>
      )}

      {isWanle && (
        <div role="note" data-testid="wanle-note" className="mb-3.5 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-[12.5px] text-sky-800">
          携程玩乐只收<b>一日游</b>和<b>门票</b>；多日游在「携程」页签。第一期：先在携程玩乐后台（VBK）建好商品，再在这里填编号绑定，之后库存、订单、凭证、核销都自动；价格默认不推，要在渠道中心「携程玩乐」配好合同报价方式和佣金后才推。
        </div>
      )}

      {focusProduct && activePlatform && (
        <OptionConnectCard platformCode={activePlatform.code} platformName={activePlatform.display_name} productId={focusProduct} preOptionId={focusOption} canManage={canManage} onClose={closeFocus} />
      )}

      {blocked && (
        <div role="alert" data-testid="listing-name-en-blocked" className="mb-3.5 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>{blocked.name}：{blocked.message}<FixEnglishLink productId={blocked.id} /></span>
        </div>
      )}

      {instantRequired && (
        <div role="note" data-testid="listing-instant-note" className="mb-3.5 flex flex-wrap items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-[12.5px] text-sky-800">
          <span>{pname} 只能上架「即时确认」的产品（平台不支持待确认；Klook 在「接口待确认」开关关闭时同样）。人工确认 / 未设置的产品勾选置灰，原因见鼠标悬停。</span>
          <button type="button" data-testid="attention-filter" aria-pressed={onlyAttention} onClick={() => setOnlyAttention((v) => !v)}
            className={`ml-auto rounded-full px-3 py-0.5 text-xs font-semibold ${onlyAttention ? "bg-rose-600 text-white" : "border border-rose-300 bg-white text-rose-700"}`}>需处理 {attentionCount}</button>
        </div>
      )}

      {instantBlocked && (
        <div role="alert" data-testid="listing-instant-blocked" className="mb-3.5 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>{instantBlocked.name}：{instantBlocked.message}<Link to={`/products/${instantBlocked.id}`} className="ml-2 font-medium underline">去产品详情设置</Link></span>
        </div>
      )}

      {notice && (
        <div role="status" data-testid="listing-mapping-warning" className="mb-3.5 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-700">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>{notice.name}：{notice.warnings.join("；")}</span>
        </div>
      )}

      {!activeCode ? (
        <div className="py-8 text-center text-[13px] text-slate-400">还没有接入任何平台（请老板 / 技术管理员先在渠道中心新增平台）</div>
      ) : mappingsQ.isLoading ? (
        <div className="p-8 text-center text-[13px] text-slate-500">加载中...</div>
      ) : mappingsQ.isError ? (
        <button type="button" onClick={() => mappingsQ.refetch()} className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-left text-[13px] font-medium text-rose-700 hover:bg-rose-100">
          加载失败，点击重试
        </button>
      ) : isWanle ? (
        <WanleBindingTable rows={rows} canManage={canManage} onChanged={refresh} />
      ) : (
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-slate-200 text-left text-[12px] font-medium text-slate-400">
              <th className="w-11 py-2">推送</th><th>产品</th><th>对接编码（住房档）</th><th>票种 <span className="ml-1 rounded border border-orange-300 bg-orange-50 px-1 text-[10.5px] font-bold text-orange-800">新</span></th><th>渠道价/人</th>{isCtrip && <th>携程建档 <span className="ml-1 rounded bg-violet-600 px-1 text-[10.5px] font-bold text-white">新</span></th>}<th>平台侧挂接</th><th>状态</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isExpanded = expanded.has(row.product_id);
              const oneDayBlocked = isCtrip && (row.total_days ?? 2) <= 1 && row.overall_status !== "active";   // CTRIP-P1 · 一日游走携程玩乐，不能勾（已启用的存量不追溯）
              return (
                <>
                  <tr key={row.product_id} data-testid={oneDayBlocked ? `oneday-${row.product_code}` : undefined} className={`border-b border-slate-100 ${oneDayBlocked ? "bg-slate-50/70 text-slate-400" : ""}`}>
                    <td className="py-2.5">
                      {/* CONFIRM-1 · 没勾即时确认的产品在要求即时确认的平台上：「建映射」置灰 + 悬停说明（已上架的不受影响，可以取消） */}
                      <span title={row.blocked_reason && row.overall_status !== "active" ? row.blocked_reason : undefined} data-testid={row.blocked_reason && row.overall_status !== "active" ? `blocked-${row.product_code}` : undefined}>
                        <Checkbox on={row.overall_status === "active"} readOnly={!canManage || (!!row.blocked_reason && row.overall_status !== "active")} onClick={() => handleCheckToggle(row)} />
                      </span>
                    </td>
                    <td>
                      <div className="font-semibold text-slate-900">{row.product_name}</div>
                      <div className="text-[12px] text-slate-400">{row.product_code}{isCtrip && row.total_days ? ` · ${row.total_days} 天` : ""}</div>
                      {oneDayBlocked && <div className="mt-0.5 text-[12px] text-slate-400">一日游走携程「玩乐」，未接入</div>}
                      {row.instant_required && (
                        <div className="mt-0.5 flex flex-wrap items-center gap-1" data-testid={`confirm-info-${row.product_code}`}>
                          <span className={`rounded-full px-1.5 py-px text-[11px] font-semibold ${row.confirm_mode === "instant" ? "bg-emerald-50 text-emerald-700" : row.confirm_mode === "manual" ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-600"}`}>{row.confirm_mode === "instant" ? "即时确认" : row.confirm_mode === "manual" ? "人工确认" : "未设置"}</span>
                          {row.needs_attention && <span className="rounded-full bg-rose-600 px-1.5 py-px text-[11px] font-semibold text-white" data-testid={`attention-${row.product_code}`}>{row.confirm_mode === "manual" ? "此产品是人工确认" : "未设置即时确认"}</span>}
                          {(row.needs_attention || (row.blocked_reason && row.overall_status !== "active")) && <Link to={`/products/${row.product_id}`} className="text-[11.5px] font-medium text-blue-600 hover:underline">去产品详情设置</Link>}
                        </div>
                      )}
                      {row.mapped && activeCode && (
                        <button type="button" data-testid={`seat-detail-${row.product_code}`} onClick={() => setSeatRow(row)} className="mt-0.5 mr-2 inline-block text-[11.5px] font-medium text-blue-600 hover:underline">余位明细</button>
                      )}
                      {canManage && <Link to={`?${new URLSearchParams({ ...Object.fromEntries(sp), product_id: row.product_id, option_id: "" }).toString().replace("&option_id=", "")}`} replace data-testid={`option-connect-${row.product_code}`} className="mt-0.5 inline-block text-[11.5px] font-medium text-blue-600 hover:underline">按选项连接</Link>}
                    </td>
                    <td>
                      {row.has_tiers || row.extra_vehicle ? (
                        <button type="button" onClick={() => toggleExpand(row.product_id)} className="flex items-center gap-1 text-[12.5px] text-slate-500 hover:text-slate-700">
                          {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                          {!row.mapped
                            ? "勾选后自动生成 R1~R4"
                            : `${row.has_tiers ? `${row.tiers.length} 档` : "不分档"}${row.extra_vehicle?.enabled ? ` + 加售 ${row.extra_vehicle.tiers.length}` : ""}`}
                        </button>
                      ) : (
                        <span className="text-[12.5px] text-slate-500">{row.mapped ? "不分档" : `勾选后生成 ${row.product_code}（不分档）`}</span>
                      )}
                    </td>
                    <td data-testid={`tickets-${row.product_code}`}>
                      {row.mapped ? (
                        <span className="flex flex-wrap items-center gap-1">
                          {(row.categories && row.categories.length ? row.categories : ["ADULT"]).map((c) => (
                            <span key={c} className="inline-flex items-center rounded-md border border-blue-200 bg-blue-50 px-1.5 py-px text-[12px] font-semibold text-blue-600">{TICKET_CN[c] ?? c}</span>
                          ))}
                          {canManage && (
                            <button type="button" onClick={() => setTicketRow(row)} className="rounded-md border border-slate-200 bg-white px-2 py-0.5 text-[12px] text-slate-600 hover:bg-slate-50">改</button>
                          )}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="tabular-nums">
                      {row.price_min_cents == null ? "—" : row.price_min_cents === row.price_max_cents ? fmtUsd(row.price_min_cents, false) : `${fmtUsd(row.price_min_cents, false)} ~ ${fmtUsd(row.price_max_cents, false)}`}
                    </td>
                    {isCtrip && <td><CtripListingCell s={row.ctrip_listing} onOpen={() => setCtripRow(row)} /></td>}
                    <td>{row.mapped ? <Pill tone={row.bound_count === row.tier_count ? "green" : "amber"}>{row.bound_count} / {row.tier_count} 档已挂</Pill> : "—"}</td>
                    <td>
                      <Pill tone={row.overall_status === "active" ? "green" : row.overall_status === "inactive" ? "amber" : "gray"}>{row.overall_status === "active" ? (ordersOnly ? "已上架" : "推送中") : row.overall_status === "inactive" ? "已停用" : (ordersOnly ? "未上架" : "未推送")}</Pill>
                      {(() => {   // GYGPAUSE-1 · 产品行也要看得见：任一编号暂停中就标出来（不分档的产品没有可展开的档位行）
                        const paused = row.tiers.filter((t) => t.platform_inactive_since);
                        if (paused.length === 0) return null;
                        const next = paused.map((t) => t.platform_probe_after).filter(Boolean).sort()[0];
                        return (
                          <div className="mt-0.5">
                            <span data-testid={`paused-row-${row.product_code}`} title={paused[0].platform_inactive_reason ?? undefined}
                              className="inline-block rounded-md bg-amber-50 px-1.5 py-0.5 text-[11.5px] font-medium text-amber-700">
                              {pname} 尚未上线 · 已暂停推送{next ? `（下次试探 ${fmtProbe(next as string)}）` : ""}
                            </span>
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                  {isExpanded && row.tiers.map((t) => (
                    <tr key={t.mapping_id} className="border-b border-slate-100 bg-slate-50/60 text-[12.5px] text-slate-600">
                      <td></td>
                      <td className="py-2 pl-3">└ {t.occupancy_label}</td>
                      <td><span className="rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[12px]">{t.external_product_id}</span></td>
                      <td></td>
                      <td className="tabular-nums">{fmtUsd(t.list_price_cents)}</td>
                      {isCtrip && <td></td>}
                      <td>
                        {t.platform_bound ? (
                          <span className="text-emerald-600">{ordersOnly ? `${pname} 套餐已建 ✓` : `${pname} option 已挂 ✓`}</span>
                        ) : !canManage ? (
                          <span className="text-amber-600">未挂</span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => patchMut.mutate({ mappingId: t.mapping_id, platform_bound: true })}
                            className="text-amber-600 hover:underline"
                          >
                            {ordersOnly ? `未建 · 标记为已在 ${pname} 后台建好套餐` : `未挂 · 标记为已在 ${pname} 后台建 option`}
                          </button>
                        )}
                        {t.platform_inactive_since && (
                          <div className="mt-0.5">
                            <span data-testid="paused-tag" title={t.platform_inactive_reason ?? undefined}
                              className="inline-block rounded-md bg-amber-50 px-1.5 py-0.5 text-[11.5px] font-medium text-amber-700">
                              {pname} 尚未上线 · 已暂停推送{t.platform_probe_after ? `（下次试探 ${fmtProbe(t.platform_probe_after)}）` : ""}
                            </span>
                          </div>
                        )}
                      </td>
                      <td></td>
                    </tr>
                  ))}
                  {isExpanded && row.extra_vehicle && (
                    <tr key={`${row.product_id}-extra`} className="border-b border-slate-100 bg-slate-50/60">
                      <td></td>
                      <td colSpan={isCtrip ? 7 : 6} className="py-2">
                        <ExtraVehicleRow
                          row={row} platformName={activePlatform?.display_name ?? ""} busy={extraMut.isPending} canManage={canManage}
                          onToggle={(enabled) => extraMut.mutate({ row, enabled })}
                          onMarkBound={(mappingId) => patchMut.mutate({ mappingId, platform_bound: true })}
                        />
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      )}

      {ctripRow && isCtrip && ctripOption(ctripRow) && (
        <CtripListingDrawer
          optionId={ctripOption(ctripRow)!.option_id} productId={ctripRow.product_id} optionCode={ctripOption(ctripRow)!.option_code} productName={ctripRow.product_name} canManage={canManage}
          onClose={() => setCtripRow(null)} onSubmitted={onCtripSubmitted} onBindChanged={refresh}
        />
      )}

      {seatRow && activeCode && (
        <SeatDetailModal platformCode={activeCode} productId={seatRow.product_id} productName={seatRow.product_name} productCode={seatRow.product_code} onClose={() => setSeatRow(null)} />
      )}

      {ticketRow && activeCode && (
        <TicketDrawer
          row={ticketRow} platformCode={activeCode}
          onClose={() => setTicketRow(null)}
          onSaved={() => { setTicketRow(null); refresh(); }}
        />
      )}

      <div className="mt-3 text-[11px] text-slate-400">
        勾选/取消 = 映射行启停 · 勾选新产品 = 生成映射（含 4 档，重复勾选幂等不重复生成）
      </div>
    </div>
  );
}
